import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { SPECTRUM_COLORS } from '../materials/SpectrumMaterial';

// Shared geometries - created once, reused
const sharedCircleGeometry = new THREE.CircleGeometry(1, 6);
const sharedPlaneGeometry = new THREE.PlaneGeometry(10, 0.9);
const sharedCenterLineGeometry = new THREE.PlaneGeometry(10, 0.15);

// Temp objects for matrix calculations
const tempMatrix = new THREE.Matrix4();
const tempPosition = new THREE.Vector3();
const tempScale = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();

const LineClearEffect = ({ rows, linesCleared, onComplete }) => {
  const groupRef = useRef();
  const flashRefs = useRef([]);
  const sparkleInstanceRef = useRef();
  const startTime = useRef(Date.now());

  const accentColor = useMemo(() => new THREE.Color(SPECTRUM_COLORS.accent), []);
  const whiteColor = useMemo(() => new THREE.Color(0xffffff), []);

  // Generate sparkle data
  const sparkleData = useMemo(() => {
    const data = [];
    const sparklePerLine = 15;

    rows.forEach((row) => {
      const baseY = 20 - row - 0.5;
      for (let i = 0; i < sparklePerLine; i++) {
        data.push({
          x: (i / sparklePerLine) * 10 - 5 + (Math.random() - 0.5) * 0.5,
          y: baseY + (Math.random() - 0.5) * 0.3,
          z: 0.3 + Math.random() * 0.2,
          delay: i * 0.02 + Math.random() * 0.05,
          size: 0.08 + Math.random() * 0.06,
          isWhite: Math.random() < 0.33,
        });
      }
    });
    return data;
  }, [rows]);

  const sparkleCount = sparkleData.length;

  // Create instanced material
  const sparkleMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: accentColor,
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), [accentColor]);

  // Initialize instance matrices
  useEffect(() => {
    if (sparkleInstanceRef.current) {
      sparkleData.forEach((data, i) => {
        tempPosition.set(data.x, data.y, data.z);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        sparkleInstanceRef.current.setMatrixAt(i, tempMatrix);
        // Set color per instance
        sparkleInstanceRef.current.setColorAt(i, data.isWhite ? whiteColor : accentColor);
      });
      sparkleInstanceRef.current.instanceMatrix.needsUpdate = true;
      sparkleInstanceRef.current.instanceColor.needsUpdate = true;
    }
  }, [sparkleData, accentColor, whiteColor]);

  // Single consolidated useFrame for all animations
  useFrame(() => {
    const elapsed = (Date.now() - startTime.current) / 1000;
    const duration = 0.6;

    if (elapsed > duration) {
      onComplete?.();
      return;
    }

    const progress = elapsed / duration;

    // Animate flash bars - sweep from left to right then fade
    flashRefs.current.forEach((flash) => {
      if (!flash) return;
      const sweepProgress = Math.min(1, elapsed * 4);
      flash.scale.x = sweepProgress;
      flash.position.x = -5 + sweepProgress * 5;
      flash.material.opacity = (1 - progress) * 0.7;
    });

    // Animate sparkles using instanced mesh
    if (sparkleInstanceRef.current) {
      sparkleData.forEach((data, i) => {
        const localProgress = Math.max(0, (elapsed - data.delay) / (duration - data.delay));

        if (localProgress > 0 && localProgress < 1) {
          // Pop up and float
          const y = data.y + localProgress * 0.8;
          const z = data.z + localProgress * 0.3;
          // Scale up then down
          const scaleProgress = localProgress < 0.3 ? localProgress / 0.3 : 1 - (localProgress - 0.3) / 0.7;
          const scale = data.size * scaleProgress * 3;

          tempPosition.set(data.x, y, z);
          tempScale.setScalar(scale);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          sparkleInstanceRef.current.setMatrixAt(i, tempMatrix);
        } else {
          // Hide by setting scale to 0
          tempPosition.set(data.x, data.y, data.z);
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          sparkleInstanceRef.current.setMatrixAt(i, tempMatrix);
        }
      });
      sparkleInstanceRef.current.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <group ref={groupRef}>
      {/* Sweeping flash bars - kept as individual meshes (only 1-4 per effect) */}
      {rows.map((row, i) => (
        <mesh
          key={`flash-${i}`}
          ref={(el) => (flashRefs.current[i] = el)}
          position={[-5, 20 - row - 0.5, 0.15]}
          geometry={sharedPlaneGeometry}
        >
          <meshBasicMaterial
            color={accentColor}
            transparent
            opacity={0.7}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* White center line - kept as individual meshes (only 1-4 per effect) */}
      {rows.map((row, i) => (
        <mesh
          key={`center-${i}`}
          position={[0, 20 - row - 0.5, 0.2]}
          geometry={sharedCenterLineGeometry}
        >
          <meshBasicMaterial
            color={whiteColor}
            transparent
            opacity={0.9}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Sparkles - Instanced for performance */}
      {sparkleCount > 0 && (
        <instancedMesh
          ref={sparkleInstanceRef}
          args={[sharedCircleGeometry, sparkleMaterial, sparkleCount]}
          frustumCulled={false}
        />
      )}
    </group>
  );
};

export default LineClearEffect;
